import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import api from 'api';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import PortfolioDealsEmptyState from 'components/PortfolioDealsEmptyState';
import PortfolioHeading from '../../../../components/PortfolioHeading';
import ListingPortfolioCard from 'components/ListingPortfolioCard';
import { PortfolioQueryParams } from '../../../../subrouters/Buyer/models';
import useQueryParams from 'hooks/useQueryParams';
import { DealRoom } from 'models/DealRoom';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  PortfolioSellerTable,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  TABLE_COLUMNS,
  CUSTOM_COL_SIZES,
  ENTERPRISE_TABLE_COLUMNS,
  BOSS_MODE_TABLE_COLUMNS,
  LCW_HEADING_TRANSLATIONS,
  REQUIRED_COLUMN,
} from './components/SellerDealTableCells/data';
import { BODY_CONTENT_GENERATE } from './components/SellerDealTableCells';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { PortfolioSellerFilters } from '../../../../components/PortfolioSellerFilters';
import { useNavigate, Link } from 'react-router-dom';
import { FIRST_PAGE } from 'data/global';
import { ViewSelect } from '../../../../components/ViewSelect';
import { EnterpriseSellerEmptyState } from './components/EntepriseSellerEmptyState';
import useActiveBanner from './hooks/useActiveBanner';
import { useTranslation } from 'react-i18next';
import { TABLES_WITH_COLUMN_SETTINGS } from 'models/User';
import useTableColumnSettings from 'components/ReorderResizeTable/hooks/useTableColumnSettings';
import useSearchParams from 'hooks/useSearchParams';
import ResolvePagination from 'components/ResolvePagination';
import { PF_GRID_VIEW_LIMIT, SELLING_DEFAULT_SORT } from '../../../data';
import PortfolioPlaceholderCards from '../../../Buyer/components/PortfolioPlaceholderCards';
import PortfolioSellerOptions from '../../../../components/PortfolioSellerOptions';
import {
  PORTFOLIO_FILTER,
  PortfolioFilter,
} from 'router/subrouters/Dash/subrouters/hooks/useBusinessesFilters';
import {
  getIsListingOwner,
  safeStringify,
  downloadUrlInNewWindow,
  getIsArrayEmpty,
  getHasActiveFilters,
  getFilterOptionByValue,
} from 'utils';
import { DropzoneFile } from 'components/Dropzone/utils';
import useMultiSort from 'hooks/useMultiSort';
import {
  ASCENDING_DESCENDING_COPY,
  EARLIEST_LATEST_COPY,
  INITIAL_ADVANCED_COPY,
} from 'models/Sort';
import useNumberOfResultsPerPage from 'hooks/useNumberOfResultsPerPage';
import useUserEnterpriseRoute from 'hooks/useUserEnterpriseRoute';
import MultiSortPanel from 'components/MultiSortPanel';
import { getApiRangesFromParams } from 'components/RangeFilterWrapper/utils';
import usePortfolioSellerFilterOptions from '../../../../components/PortfolioSellerFilters/usePortfolioSellerFilterOptions';
import ActiveFilterSortPills from 'components/ActiveFilterSortPills';
import NumberOfResultsPerPageFilter from 'components/NumberOfResultsPerPageFilter';
import useTableFetchState from 'hooks/useTableFetchState';
import DownloadStatusContext from 'providers/DownloadStatus/DownloadStatus.context';
import { DOWNLOAD_BANNER_STATUS, DOWNLOAD_BANNER_TYPE } from 'models/Download';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import DownloadIcon from 'icons/Download.icon';
import TableControlButton from 'components/TableControlButton';
import useChipLabelClickRefs from 'hooks/useChipLabelClickRefs';
import { ColumnFilterOption } from 'components/ReorderResizeTable/types';
import {
  DEAL_STATUS_FILTER_KEY,
  DEAL_TO_FILTER_KEY,
  LISTING_STATUS_FILTER_KEY,
} from '../../../../components/PortfolioSellerFilters/data';
import { LOCATION_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/LocationFilter/LocationFilter.component';
import { INDUSTRY_FILTER_KEY } from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/IndustryFilter/IndustryFilter.component';
import ManageColumns from 'components/ManageColumns';

import './SellerBrowsePortfolio.styles.scss';
import './SellerBrowsePortfolio.styles.responsive.scss';

const MULTI_SORT_LABEL_TRANSLATIONS = {
  ...HEADING_TRANSLATIONS,
  filesSort: 'numberOfFiles',
  buyersSort: 'numberOfLeads',
  commission: 'commission',
};

const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.stage]: INITIAL_ADVANCED_COPY,
  [SORTING_VALUES.listingStage]: INITIAL_ADVANCED_COPY,
  [SORTING_VALUES.lastUpdated]: EARLIEST_LATEST_COPY,
  [SORTING_VALUES.price]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.commission]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.buyersSort]: ASCENDING_DESCENDING_COPY,
  [SORTING_VALUES.filesSort]: ASCENDING_DESCENDING_COPY,
};

type SellerBrowsePortfolioProps = {
  className?: string;
};

const SellerBrowsePortfolio: React.FC<SellerBrowsePortfolioProps> = (props) => {
  const { className } = props;

  const {
    isEnterpriseHead,
    isOfficeManager,
    isSuperAdmin,
    hasEnterprise,
    isRWCMember,
    currentUser: { _id: currentUserId, isGridViewForDeals },
  } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const defaultTableColumns = useMemo(
    () =>
      isSuperAdmin
        ? BOSS_MODE_TABLE_COLUMNS
        : isEnterpriseHead || isOfficeManager
        ? ENTERPRISE_TABLE_COLUMNS
        : TABLE_COLUMNS,
    [isEnterpriseHead, isOfficeManager, isSuperAdmin],
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const {
    isLoading,
    totalPages,
    totalItems,
    data: deals,
    setIsLoading,
    setData,
    setPaginatedData,
  } = useTableFetchState<DealRoom>();

  const classes = classNames('seller-browse-portfolio', className);

  const { limit, updateLimit } = useNumberOfResultsPerPage();

  const isTableView = useMemo(() => !isGridViewForDeals, [isGridViewForDeals]);

  const {
    columns,
    allColumns,
    hiddenColumns,
    columnSizing,
    isDefaultColumns,
    onColumnSizingChange,
    onReorder,
    saveColumnSettings,
    resetToDefault,
  } = useTableColumnSettings(
    TABLES_WITH_COLUMN_SETTINGS.SELLER,
    defaultTableColumns,
  );

  const headingTranslations = useMemo(
    () => (isRWCMember ? LCW_HEADING_TRANSLATIONS : HEADING_TRANSLATIONS),
    [isRWCMember],
  );

  const displayDealNotification = useActiveBanner(deals);

  const { multiSortValues, multiSortDirectionTranslation } = useMultiSort(
    columns,
    SORTING_VALUES,
    MULTI_SORT_LABEL_TRANSLATIONS,
    MULTI_SORT_DIRECTION_COPY,
  );

  const filterOptions = usePortfolioSellerFilterOptions();

  const columnFilterOptions = useMemo(() => {
    const columnSortOptions = columns.reduce((acc, column) => {
      const sortValue = SORTING_VALUES[column];

      if (MULTI_SORT_DIRECTION_COPY[sortValue])
        acc[column] = {
          sortDirectionTranslation: multiSortDirectionTranslation[sortValue],
        };

      return acc;
    }, {} as Partial<Record<keyof PortfolioSellerTable, ColumnFilterOption>>);

    return {
      ...columnSortOptions,
      stage: {
        ...columnSortOptions.stage,
        options: getFilterOptionByValue(filterOptions, DEAL_STATUS_FILTER_KEY),
      },
      listingStage: {
        ...columnSortOptions.listingStage,
        options: getFilterOptionByValue(
          filterOptions,
          LISTING_STATUS_FILTER_KEY,
        ),
      },
      price: {
        ...columnSortOptions.price,
        options: getFilterOptionByValue(filterOptions, DEAL_TO_FILTER_KEY),
      },
      location: {
        ...columnSortOptions.location,
        options: getFilterOptionByValue(filterOptions, LOCATION_FILTER_KEY),
      },
      industry: {
        ...columnSortOptions.industry,
        options: getFilterOptionByValue(filterOptions, INDUSTRY_FILTER_KEY),
      },
    } as Partial<Record<keyof PortfolioSellerTable, ColumnFilterOption>>;
  }, [columns, filterOptions, multiSortDirectionTranslation]);

  const isEmpty = useMemo(() => getIsArrayEmpty(deals), [deals]);

  const enterpriseRoute = useUserEnterpriseRoute();

  const {
    params: { sort = SELLING_DEFAULT_SORT, portfolioFilter },
    setQueryParam,
  } = useQueryParams<
    PortfolioQueryParams & { portfolioFilter?: PortfolioFilter }
  >();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    multiFilterRef,
    multiSortRef,
    onChipLabelClick,
    onSortChipLabelClick,
  } = useChipLabelClickRefs();

  const searchParams = useSearchParams();

  const hasActiveFilters = useMemo(
    () => getHasActiveFilters(filterOptions, searchParams),
    [filterOptions, searchParams],
  );

  const isPortfolioFilterMissing = useMemo(
    () =>
      !portfolioFilter && (isEnterpriseHead || isOfficeManager || isSuperAdmin),
    [isEnterpriseHead, isOfficeManager, isSuperAdmin, portfolioFilter],
  );

  const { showDownloadBanner } = useContext(DownloadStatusContext);

  const getMixpanelProps = useGetMixpanelProps();

  const downloadCSV = useCallback(async () => {
    try {
      setIsDownloading(true);
      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.PREPARING,
        type: DOWNLOAD_BANNER_TYPE.FILE,
        customTitle: t('preparingListingData'),
      });

      const {
        data: { url, type },
      } = await api.portfolio.admin.getDealsCsv();

      if (url) {
        showDownloadBanner({
          status: DOWNLOAD_BANNER_STATUS.SUCCESS,
          type: DOWNLOAD_BANNER_TYPE.FILE,
        });
        downloadUrlInNewWindow(url, type);
      }
    } catch (e) {
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
      );
      showDownloadBanner({
        status: DOWNLOAD_BANNER_STATUS.ERROR,
        type: DOWNLOAD_BANNER_TYPE.FILE,
      });
    } finally {
      setIsDownloading(false);
    }
  }, [getMixpanelProps, showDownloadBanner, t]);

  const updateDeal = useCallback(
    (dealId: string, values: Partial<DealRoom>) =>
      setData((old) =>
        old.map((deal) =>
          deal._id === dealId ? { ...deal, ...values } : deal,
        ),
      ),
    [setData],
  );

  const updateGallery = useCallback(
    (dealId: string, gallery: DropzoneFile[]) =>
      setData((old) =>
        old.map((deal) =>
          deal._id === dealId
            ? { ...deal, listing: { ...deal.listing, gallery } }
            : deal,
        ),
      ),
    [setData],
  );

  const renderDeals = useMemo(
    () =>
      isLoading ? (
        <PortfolioPlaceholderCards numberOfCards={PF_GRID_VIEW_LIMIT} />
      ) : (
        deals.map(
          ({
            _id,
            stage,
            listingStage,
            publishedOn,
            industry,
            dealOutcome,
            otherDealOutcome,
            portfolioName,
            isColleague,
            userId,
            user,
            priceGuide,
            amount,
            amountFrom,
            amountTo,
            franchisorFeePercentage,
            buyersSort,
            filesSort,
            isRWC,
            listingEnquiry,
            leadStagesCopies,
            listing,
            listing: { type, expirationDate, seekingExpressionOfInterestDate },
          }) => {
            const {
              _id: enquiryId,
              numberOfAcceptedLeads,
              numberOfConfirmedLeads,
              numberOfEnquiredLeads,
              numberOfOfferedLeads,
              progressedStep,
            } = listingEnquiry || {};

            return (
              <ListingPortfolioCard
                key={_id}
                title={portfolioName}
                dealStage={stage}
                listingStage={listingStage}
                date={publishedOn}
                isColleague={isColleague}
                dealOutcome={dealOutcome}
                otherDealOutcome={otherDealOutcome}
                dealRoomId={_id}
                listingType={type}
                industry={industry}
                seekingExpressionOfInterestDate={
                  seekingExpressionOfInterestDate
                }
                expirationDate={expirationDate}
                ownerId={userId}
                owner={user}
                enquiryId={enquiryId}
                numberOfAcceptedLeads={numberOfAcceptedLeads}
                numberOfConfirmedLeads={numberOfConfirmedLeads}
                numberOfEnquiredLeads={numberOfEnquiredLeads}
                numberOfOfferedLeads={numberOfOfferedLeads}
                progressedStep={progressedStep}
                leadId={null}
                listing={listing}
                priceGuide={priceGuide}
                amount={amount}
                amountFrom={amountFrom}
                amountTo={amountTo}
                franchisorFeePercentage={franchisorFeePercentage}
                numberOfLeads={buyersSort}
                numberOfFiles={filesSort}
                isRWC={isRWC}
                allowListingImageUpload={
                  isSuperAdmin ||
                  isColleague ||
                  getIsListingOwner(
                    currentUserId,
                    listing?.userId,
                    listing?.isCoOwner,
                  )
                }
                updateGallery={updateGallery}
                leadStagesCopies={leadStagesCopies}
              />
            );
          },
        )
      ),
    [currentUserId, deals, isLoading, isSuperAdmin, updateGallery],
  );

  const tableContent = useMemo(
    () =>
      defaultTableColumns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (deal: DealRoom) =>
            BODY_CONTENT_GENERATE[col]({
              deal,
              showBannerNotification: displayDealNotification(deal),
              updateDeal,
              updateGallery,
            }),
        }),
        {},
      ),
    [defaultTableColumns, displayDealNotification, updateDeal, updateGallery],
  );

  const onRowClick = useCallback(({ _id }) => navigate(_id), [navigate]);

  useEffect(() => {
    const page = +searchParams?.$page;

    if ((totalPages && totalPages < page) || page < 1)
      setQueryParam('page', FIRST_PAGE);
  }, [searchParams, setQueryParam, totalPages]);

  useEffect(() => {
    if (isPortfolioFilterMissing)
      setQueryParam(
        'portfolioFilter',
        isSuperAdmin ? PORTFOLIO_FILTER.ALL : PORTFOLIO_FILTER.MY_DEALS,
      );
  }, [isPortfolioFilterMissing, isSuperAdmin, setQueryParam]);

  useEffect(() => {
    if (isPortfolioFilterMissing) return;

    (async () => {
      try {
        setIsLoading(true);

        const { to, from, userId, colleagueIds, ...restParams } = searchParams;

        const priceRanges = getApiRangesFromParams(from, to);

        const { data } = await api.dealRoom.getDealsSeller({
          $limit: `${isTableView ? limit : PF_GRID_VIEW_LIMIT}`,
          $relations: '["listing","listingEnquiry","user"]',
          priceRanges: safeStringify(priceRanges),
          ...(userId && colleagueIds
            ? {
                $or: `[{"userId": "${userId}"}, {"colleagueIds": "${colleagueIds}"}]`,
              }
            : {}),
          ...restParams,
          sort,
        });

        setPaginatedData(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [
    isTableView,
    searchParams,
    sort,
    isPortfolioFilterMissing,
    limit,
    popServerError,
    setPaginatedData,
    setIsLoading,
  ]);

  return (
    <div className={classes}>
      <div className="browse-portfolio-heading">
        <div className="browse-portfolio-heading__left">
          <PortfolioHeading />

          {enterpriseRoute && (
            <Link to={enterpriseRoute}>{t('goToYouEnterpriseDashboard')}</Link>
          )}
          <PortfolioSellerOptions />
        </div>

        {(!isEmpty || isLoading || hasActiveFilters) && (
          <div className="browse-portfolio-heading__right">
            {isTableView && !isEmpty && (
              <NumberOfResultsPerPageFilter
                limit={limit}
                updateLimit={updateLimit}
              />
            )}
            <PortfolioSellerFilters ref={multiFilterRef} />
            <MultiSortPanel
              ref={multiSortRef}
              options={multiSortValues}
              sortDirectionTranslations={multiSortDirectionTranslation}
            />

            {isTableView && (
              <ManageColumns
                allColumns={allColumns}
                hiddenColumns={hiddenColumns}
                headingTranslations={headingTranslations}
                lockedColumn={REQUIRED_COLUMN}
                isDefaultColumns={isDefaultColumns}
                onSave={saveColumnSettings}
                onResetToDefault={resetToDefault}
              />
            )}

            {isSuperAdmin && (
              <TableControlButton
                className="download-listing-data"
                icon={DownloadIcon}
                tooltip={t('downloadListingData')}
                disabled={isDownloading}
                onClick={downloadCSV}
              />
            )}

            <ViewSelect
              updateProp="isGridViewForDeals"
              isTableView={isTableView}
            />
          </div>
        )}
      </div>

      <ActiveFilterSortPills
        filterOptions={filterOptions}
        sortOptions={multiSortValues}
        onChipLabelClick={onChipLabelClick}
        onSortChipLabelClick={onSortChipLabelClick}
      />

      <div className="resolve-page-items">
        {!isEmpty || isLoading ? (
          !isTableView ? (
            <>
              {renderDeals}
              <ResolvePagination totalPages={totalPages} />
            </>
          ) : (
            <ReorderResizeTable
              className="portfolio-results-table"
              headingTranslations={headingTranslations}
              tableContentTransformations={tableContent}
              columns={columns}
              sortingValues={SORTING_VALUES}
              columnFilterOptions={columnFilterOptions}
              isDataLoading={isLoading}
              customSizes={CUSTOM_COL_SIZES}
              totalPages={totalPages}
              totalItems={totalItems}
              recordName="deal"
              data={deals as unknown as PortfolioSellerTable[]}
              onRowClick={onRowClick}
              onReorder={onReorder}
              columnSizing={columnSizing}
              onColumnSizingChange={onColumnSizingChange}
            />
          )
        ) : hasEnterprise ? (
          <EnterpriseSellerEmptyState />
        ) : (
          <PortfolioDealsEmptyState />
        )}
      </div>
    </div>
  );
};

export default SellerBrowsePortfolio;
